import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import logoSite from "../../../../assets/brand/logo_site.svg";
import logoPurple from "../../../../assets/brand/logo_site_purple.svg";

/**
 * Two little "what it does" loops that sit UNDER the CherryPick / Black Cherry cards.
 *
 * SelectClipsLoop  — the personal way: a cursor ticks clips one by one (pure CSS, fixed height).
 * BlackCherryLoop  — the AI way: a persona chip + a chat pill that types prompts, holds, clears,
 *                    and types the next one (JS typewriter). No timers based on Date/random.
 *
 * Both are self-contained dark "screens", so they read fine inside the light or dark plugin page.
 */

const font = "Outfit, sans-serif";

/* ─────────────────── CherryPick: cursor ticks clips ─────────────────── */
const CYCLE = "6s";
const SEL_ROWS = [
  { text: "This is the moment everything changed.", at: 34 },
  { text: "Let me show you exactly how it works.", at: 52 },
  { text: "I almost gave up right here.", at: 70 },
];

export const SelectClipsLoop: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      width: "100%", height: "168px", borderRadius: "12px", overflow: "hidden",
      border: "1px solid rgba(230,29,80,0.45)", background: "#0d0c12", p: "12px", position: "relative",
      boxShadow: "0 0 0 1px rgba(230,29,80,0.12), 0 16px 44px -22px rgba(230,29,80,0.6)",
    }}
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {SEL_ROWS.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: "flex", alignItems: "center", gap: "9px", borderRadius: "7px",
            borderLeft: "3px solid transparent", pl: "6px", py: "5px",
            animation: `ccpSel${i} ${CYCLE} ease-in-out infinite`,
            [`@keyframes ccpSel${i}`]: {
              "0%": { backgroundColor: "rgba(230,29,80,0)", borderLeftColor: "rgba(230,29,80,0)" },
              [`${row.at - 0.3}%`]: { backgroundColor: "rgba(230,29,80,0)", borderLeftColor: "rgba(230,29,80,0)" },
              [`${row.at}%`]: { backgroundColor: "rgba(230,29,80,0.2)", borderLeftColor: "#e61d50" },
              "94%": { backgroundColor: "rgba(230,29,80,0.2)", borderLeftColor: "#e61d50" },
              "100%": { backgroundColor: "rgba(230,29,80,0)", borderLeftColor: "rgba(230,29,80,0)" },
            },
          }}
        >
          {/* checkbox ticks on at the click */}
          <Box
            sx={{
              width: "16px", height: "16px", borderRadius: "4px", flexShrink: 0,
              border: "1.5px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center",
              animation: `ccpBox${i} ${CYCLE} ease-in-out infinite`,
              [`@keyframes ccpBox${i}`]: {
                "0%": { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.3)" },
                [`${row.at - 0.3}%`]: { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.3)" },
                [`${row.at}%`]: { backgroundColor: "#e61d50", borderColor: "#e61d50" },
                "94%": { backgroundColor: "#e61d50", borderColor: "#e61d50" },
                "100%": { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.3)" },
              },
            }}
          >
            <Box component="span" sx={{ color: "#fff", fontSize: "10px", fontWeight: 900, lineHeight: 1, opacity: 0, animation: `ccpMark${i} ${CYCLE} ease-in-out infinite`, [`@keyframes ccpMark${i}`]: { "0%": { opacity: 0 }, [`${row.at - 0.3}%`]: { opacity: 0 }, [`${row.at}%`]: { opacity: 1 }, "94%": { opacity: 1 }, "100%": { opacity: 0 } } }}>✓</Box>
          </Box>
          {/* logo thumbnail */}
          <Box sx={{ position: "relative", width: "58px", aspectRatio: "16 / 9", borderRadius: "5px", flexShrink: 0, background: "linear-gradient(160deg,#2c2533,#181420)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={logoSite} alt="" style={{ height: "44%", width: "auto", opacity: 0.95 }} />
          </Box>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "11.5px", color: "#e8eaf3", lineHeight: 1.3 }}>{row.text}</Typography>
        </Box>
      ))}
    </Box>
    {/* one cursor ticking each clip in turn */}
    <Box
      sx={{
        position: "absolute", top: 0, left: 0, width: "20px", height: "20px", zIndex: 4, pointerEvents: "none",
        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.55))",
        animation: `ccpCursor ${CYCLE} ease-in-out infinite`,
        "@keyframes ccpCursor": {
          "0%": { opacity: 0, transform: "translate(120px,150px)" },
          "10%": { opacity: 1, transform: "translate(120px,150px)" },
          "30%": { opacity: 1, transform: "translate(20px,24px)" },
          "34%": { opacity: 1, transform: "translate(20px,24px) scale(0.82)" },
          "36%": { opacity: 1, transform: "translate(20px,24px)" },
          "50%": { opacity: 1, transform: "translate(20px,66px)" },
          "52%": { opacity: 1, transform: "translate(20px,66px) scale(0.82)" },
          "54%": { opacity: 1, transform: "translate(20px,66px)" },
          "68%": { opacity: 1, transform: "translate(20px,108px)" },
          "70%": { opacity: 1, transform: "translate(20px,108px) scale(0.82)" },
          "72%": { opacity: 1, transform: "translate(20px,108px)" },
          "94%": { opacity: 1, transform: "translate(20px,108px)" },
          "100%": { opacity: 0, transform: "translate(20px,108px)" },
        },
      }}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
        <path d="M5.5 2.6 L5.5 17.8 L9.3 14.1 L11.8 19.8 L14.1 18.8 L11.6 13.2 L16.8 13.2 Z" fill="#fff" stroke="#0e0e12" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
    </Box>
  </Box>
);

/* ─────────────────── Black Cherry: persona + typewriter prompts ─────────────────── */
const PERSONAS = ["Film editor", "Podcast producer", "Hype cutter"];
const PROMPTS = ["make me a trailer", "give me the best lines", "find the funniest moments", "cut a 30-second teaser"];

export const BlackCherryLoop: React.FC = () => {
  const [typed, setTyped] = useState("");
  const iRef = useRef(0); // which prompt
  const cRef = useRef(0); // which char
  const phaseRef = useRef<"type" | "hold" | "delete">("type");
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const tick = () => {
      const full = PROMPTS[iRef.current];
      const phase = phaseRef.current;
      let delay = 70;
      if (phase === "type") {
        cRef.current += 1;
        setTyped(full.slice(0, cRef.current));
        if (cRef.current >= full.length) { phaseRef.current = "hold"; delay = 1800; }
      } else if (phase === "hold") {
        phaseRef.current = "delete"; delay = 400;
      } else {
        cRef.current -= 1;
        setTyped(full.slice(0, Math.max(0, cRef.current)));
        delay = 34;
        if (cRef.current <= 0) { phaseRef.current = "type"; iRef.current = (iRef.current + 1) % PROMPTS.length; delay = 380; }
      }
      timerRef.current = window.setTimeout(tick, delay);
    };
    timerRef.current = window.setTimeout(tick, 600);
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, []);

  return (
    <Box sx={{ width: "100%", height: "168px", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(139,92,246,0.55)", background: "#0d0c12", p: "12px", display: "flex", flexDirection: "column", gap: "12px", boxShadow: "0 0 0 1px rgba(139,92,246,0.14), 0 16px 44px -22px rgba(139,92,246,0.6)" }}>
      {/* persona chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9a86d8" }}>Persona</Typography>
        {PERSONAS.map((p, i) => (
          <Box key={p} sx={{ fontFamily: font, fontSize: "11px", fontWeight: 600, px: "9px", py: "4px", borderRadius: "999px", color: i === 0 ? "#fff" : "#b7adc9", background: i === 0 ? "linear-gradient(120deg,#7c3aed,#5a2bd0)" : "transparent", border: i === 0 ? "1px solid transparent" : "1px solid rgba(139,92,246,0.35)" }}>{p}</Box>
        ))}
      </Box>
      {/* the chat pill typing prompts */}
      <Box sx={{ mt: "auto", display: "flex", alignItems: "center", gap: "9px", px: "10px", py: "9px", borderRadius: "999px", border: "1px solid rgba(139,92,246,0.4)", background: "#120c1e" }}>
        <Box aria-hidden sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: "#160c26", border: "1px solid rgba(139,92,246,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={logoPurple} alt="" height={14} style={{ width: "auto" }} />
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#e8e0ff", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden" }}>
          {typed}
          <Box component="span" sx={{ display: "inline-block", width: "2px", height: "14px", ml: "1px", verticalAlign: "-2px", background: "#c9b8ff", animation: "ccpCaret 1s steps(1) infinite", "@keyframes ccpCaret": { "50%": { opacity: 0 } } }} />
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#8d94a8", textAlign: "center" }}>You describe it. Black Cherry cuts it.</Typography>
    </Box>
  );
};
